import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import Text from "components/Text";
import Select from "react-select";
import styles from "../AddEvent.module.scss";
import crossIcon from "assets/images/cross.svg";
import StaffDefaultImage from "assets/images/staff-default-rounded.png";
import { useAllStaffByDesignationIdAndOfficeId } from "repositories/notes-repository";
import { withTranslation } from "react-i18next";
import {
  useAllActiveOffices,
  useStaffDesignation,
} from "repositories/scheduler-repository";
import ExpandableSearchBar from "../SchedulingAssistant/components/ExpandableSearchBar";
import { useSelector } from "react-redux";
import constants from "../../../../constants";
import Empty from "components/Empty";

/**
 * Stable React key for a staff row. The same person can appear in multiple
 * offices, so user, office, and record id are all included.
 * @param {object} member
 * @returns {string}
 */
function getStaffMemberRowKey(member) {
  const userId = member?.user?.id ?? member?.userId ?? "no-user";
  const officeId = member?.office?.id ?? member?.officeId ?? "no-office";
  const recordId = member?.id ?? "no-id";
  return `${recordId}-${userId}-${officeId}`;
}

/**
 * Hidden custom checkboxes scroll into view when focused, which jumps the
 * modal list to the top. Skip focus on mouse click; keyboard still works.
 * @param {React.MouseEvent<HTMLInputElement>} event
 */
function preventCheckboxFocusScroll(event) {
  event.preventDefault();
}

function SelectStaffMemberModal({
  t,
  isOpen,
  closeStepOneModal,
  ownerId,
  setMemberIds,
  memberIds,
  forEdit,
  eventOrShiftTab = false,
  officeId,
  setSelectedStaff,
  IsOwnerInclude = false,
  canAddShift = false,
  isFromBusySlot = false,
  eventEmployeesSelected = [],
  isFromEditBusySLot = false,
  optionalSelectedOfficeIds = [],
  isDistinct = false,
  externalStaffList = undefined,
  externalStaffLoading = false,
  policyId = undefined,
}) {
  // State management
  const [search, setSearch] = useState("");

  // Dropdown data
  const [rolesData, setRolesData] = useState([]);
  const [officeList, setOfficeList] = useState([]);

  // Selection states
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedOffice, setSelectedOffice] = useState([]);
  const [memberDataCache, setMemberDataCache] = useState({});
  const [schedularEventId, setSchedularEventId] = useState(null);

  // Default dropdown options
  const [selectedRoleOption, setSelectedRoleOption] = useState({
    value: "",
    label: "All Roles",
  });
  const [selectedOfficeOption, setSelectedOfficeOption] = useState({
    value: "",
    label: "All Offices",
  });
  const profile = useSelector((state) => state.userProfile.profile);

  // Member data
  const [memberList, setMemberList] = useState([]);

  // Add local state for staff selection in modal
  const [localSelectedStaff, setLocalSelectedStaff] = useState([]);

  const useExternalStaff = Array.isArray(externalStaffList);

  // Data fetching: prefer modal dropdown selection when user has selected office; otherwise optionalSelectedOfficeIds / officeId / selectedOffice
  const officeIds =
    selectedOffice.length > 0
      ? selectedOffice
      : Array.isArray(optionalSelectedOfficeIds) &&
          optionalSelectedOfficeIds.length > 0
        ? optionalSelectedOfficeIds
        : isFromBusySlot
          ? selectedOffice.length !== 0
            ? selectedOffice
            : officeId
          : officeId
            ? [officeId]
            : selectedOffice;
  const { data: internalData, isLoading: internalLoading } =
    useAllStaffByDesignationIdAndOfficeId(
      selectedRoles,
      officeIds,
      {
        ownerId,
        IsOwnerInclude,
        includeAdminOffice: canAddShift,
        isDistinct,
      },
      {
        enabled: !!ownerId && !useExternalStaff,
      },
    );

  const isLoading = useExternalStaff ? externalStaffLoading : internalLoading;
  const data = useExternalStaff
    ? { data: externalStaffList || [] }
    : internalData;

  const { isLoading: loadingRoleData, data: staffRoleData } =
    useStaffDesignation();

  const { isLoading: officeDataLoading, data: officeData } =
    useAllActiveOffices(1, constants.ACTIVE_OFFICES_PAGE_SIZE, {
      ownerId,
      includeAdminOffice: canAddShift,
    });

  useEffect(() => {
    if (forEdit) {
      setSchedularEventId(memberIds[0]?.schedulerEventId);
    }
  }, []);

  // Process office data when it's loaded
  useEffect(() => {
    if (!officeDataLoading && officeData?.data?.length) {
      const formattedData = officeData.data.map((item) => ({
        label: item?.name,
        value: item?.id,
      }));
      const filteredData =
        (isFromBusySlot &&
          formattedData.filter((item) => officeId.includes(item.value))) ||
        [];
      const finalData = isFromBusySlot ? filteredData : formattedData;
      setOfficeList([{ value: "", label: "All Offices" }, ...finalData]);
    }
  }, [officeDataLoading, officeData]);

  // Process role data when it's loaded
  useEffect(() => {
    if (!loadingRoleData && staffRoleData?.length) {
      const flatData = staffRoleData?.map((item) => item?.designations)?.flat();

      if (flatData?.length) {
        const formattedData = flatData.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));

        setRolesData([{ value: "", label: "All Roles" }, ...formattedData]);
      }
    }
  }, [loadingRoleData, staffRoleData]);

  // Process member data when fetched. When using external list, filter by Office and Role dropdowns client-side.
  useEffect(() => {
    let list = useExternalStaff ? externalStaffList || [] : (data?.data ?? []);
    if (!useExternalStaff && !data?.data) return;
    if (useExternalStaff && !Array.isArray(externalStaffList)) return;

    if (useExternalStaff && list.length > 0) {
      if (selectedOfficeOption?.value) {
        list = list.filter((member) => {
          const officeIdVal = member?.office?.id ?? member?.officeId;
          return Number(officeIdVal) === Number(selectedOfficeOption.value);
        });
      }
      if (selectedRoleOption?.value) {
        list = list.filter((member) => {
          const designationId =
            member?.designation?.id ?? member?.designationId;
          return (
            designationId != null &&
            Number(designationId) === Number(selectedRoleOption.value)
          );
        });
      }
    }

    const newCache = { ...memberDataCache };
    list.forEach((member) => {
      newCache[member.id] = member;
    });
    const filteredData =
      (isFromBusySlot && list.filter((item) => item?.userId !== profile?.id)) ||
      [];
    const finalData = isFromBusySlot ? filteredData : list;
    setMemberDataCache(newCache);
    setMemberList(finalData);
  }, [
    useExternalStaff ? externalStaffList : data,
    useExternalStaff ? selectedOfficeOption?.value : null,
    useExternalStaff ? selectedRoleOption?.value : null,
  ]);

  // Sync selection from the parent only when the modal opens. Re-running on
  // every memberIds identity change would wipe an in-progress selection.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened) {
      setLocalSelectedStaff(
        memberIds?.filter(
          (member) => member.IsDeleted === false || member.isDeleted === false,
        ) || [],
      );
    }
  }, [isOpen, memberIds]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearch("");

      if (officeData?.data?.length && officeList.length <= 1) {
        const formattedData = officeData.data.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));

        const filteredData =
          (isFromBusySlot &&
            formattedData.filter((item) => officeId?.includes(item.value))) ||
          [];
        const finalData = isFromBusySlot ? filteredData : formattedData;

        setOfficeList([{ value: "", label: "All Offices" }, ...finalData]);
      }

      if (staffRoleData?.length && rolesData.length <= 1) {
        const flatData = staffRoleData
          ?.map((item) => item?.designations)
          ?.flat();

        if (flatData?.length) {
          const formattedData = flatData.map((item) => ({
            label: item?.name,
            value: item?.id,
          }));

          setRolesData([{ value: "", label: "All Roles" }, ...formattedData]);
        }
      }
    }
  }, [isOpen, officeData, staffRoleData, officeList.length, rolesData.length]);

  const handleOfficeChange = (selected) => {
    setSelectedOfficeOption(selected);
    setSelectedOffice(selected?.value ? [selected.value] : []);
  };

  const handleRoleChange = (selected) => {
    setSelectedRoleOption(selected);
    setSelectedRoles(selected?.value ? [selected.value] : []);
  };

  const onSearch = (val) => {
    setSearch(val);

    if (!data?.data) return;

    const allMembers = [...data?.data];
    const trimmedSearchValue = val.replace(/\s+/g, "").toLowerCase();

    const filterArray = allMembers.filter((item) => {
      const fullName =
        `${item?.user?.firstName}${item?.user?.lastName}`.toLowerCase();
      return fullName.includes(trimmedSearchValue);
    });

    const filteredData =
      (isFromBusySlot &&
        filterArray?.filter((item) => item?.userId !== profile?.id)) ||
      [];
    const finalData = isFromBusySlot ? filteredData : filterArray;

    setMemberList(finalData);
  };

  // Use localSelectedStaff for selection logic
  // Coerce ids to number so API number/string mismatch doesn't prevent checkbox from showing checked (e.g. Policy/FilterOptions/Staff).
  const isSelected = (member) => {
    return localSelectedStaff.some((item) => {
      if (!isFromEditBusySLot && isFromBusySlot && member.id) {
        return Number(item?.id) === Number(member?.id);
      }
      const itemUserId = item?.user?.id ?? item?.userId;
      const memberUserId = member?.user?.id ?? member?.userId;
      if (itemUserId == null || memberUserId == null) return false;
      if (Number(itemUserId) !== Number(memberUserId)) return false;
      const itemOfficeId = item?.office?.id ?? item?.officeId;
      const memberOfficeId = member?.office?.id ?? member?.officeId;
      if (itemOfficeId != null && memberOfficeId != null) {
        return Number(itemOfficeId) === Number(memberOfficeId);
      }
      return true;
    });
  };

  const shouldDisableMember = (member) => {
    if (isFromBusySlot) {
      if (!member.id) {
        if (isSelected(member)) {
          return false;
        }
      }
      return false;
    }
    if (eventOrShiftTab && localSelectedStaff.length) {
      if (localSelectedStaff[0]?.officeId) {
        return localSelectedStaff[0]?.officeId !== member?.officeId;
      }
      return localSelectedStaff[0]?.office?.id !== member?.office?.id;
    }
    if (isSelected(member)) {
      return false;
    }
    const userId = member.user?.id || member.userId;
    if (!userId) return false;
    return localSelectedStaff.some((item) => {
      const itemUserId = item.user?.id || item.userId;
      return itemUserId === userId;
    });
  };

  const visibleSelectableMembers = memberList.filter(
    (member) => !shouldDisableMember(member),
  );
  const selectAll =
    visibleSelectableMembers.length > 0 &&
    Array.from(
      new Set(
        visibleSelectableMembers.map(
          (member) => member.user?.id || member.userId,
        ),
      ),
    ).every((userId) =>
      localSelectedStaff.some(
        (item) => (item.user?.id || item.userId) === userId,
      ),
    );

  const handleSelectUser = (staffMember) => {
    if (isSelected(staffMember)) {
      const removeUserId = Number(staffMember?.user?.id ?? staffMember?.userId);
      setLocalSelectedStaff((prev) =>
        prev.filter((item) => {
          if (isFromBusySlot && !staffMember.id) {
            if (Number(item?.userId) === removeUserId || !item.id) {
              return (
                Number(item?.officeId ?? item?.office?.id) !==
                Number(staffMember?.officeId ?? staffMember?.office?.id)
              );
            }
            return Number(item?.id) !== Number(staffMember?.id);
          }
          if (!isFromEditBusySLot && isFromBusySlot) {
            return Number(item?.id) !== Number(staffMember?.id);
          }
          return Number(item?.user?.id ?? item?.userId) !== removeUserId;
        }),
      );
    } else {
      const userId = staffMember.user?.id || staffMember.userId;
      let obj = {
        ...staffMember,
        userId: userId,
        IsDeleted: false,
        isDeleted: false,
      };
      if (forEdit) {
        obj.schedulerEventId = schedularEventId;
      }
      setLocalSelectedStaff((prev) => [...prev, obj]);
    }
  };

  const handleAllChange = () => {
    if (selectAll) {
      // Deselecting
      if (eventOrShiftTab || isFromBusySlot) {
        // Clear all if shift tab or from busy slot
        setLocalSelectedStaff([]);
      } else {
        // For event tab (not from busy slot): remove visible users by userId
        const visibleUserIds = new Set(
          memberList.map((member) => member.user?.id || member.userId),
        );
        setLocalSelectedStaff((prev) =>
          prev.filter(
            (item) => !visibleUserIds.has(item.user?.id || item.userId),
          ),
        );
      }
    } else {
      // Selecting
      if (eventOrShiftTab || isFromBusySlot) {
        if (isFromBusySlot) {
          // Just add all memberList (no filtering, no deduplication)
          setLocalSelectedStaff([...memberList]);
        } else {
          // For shift: select only same-office staff
          const idMatch = localSelectedStaff?.length
            ? localSelectedStaff[0].office.id
            : memberList[0].office.id;
          const allowedMembers = memberList.filter(
            (member) => idMatch === member.office.id,
          );
          setLocalSelectedStaff(allowedMembers);
        }
      } else {
        // For event tab (not from busy slot): select all unique userIds
        const uniqueUserMap = new Map();
        memberList.forEach((member) => {
          const userId = member.user?.id || member.userId;
          if (!uniqueUserMap.has(userId)) {
            uniqueUserMap.set(userId, member);
          }
        });
        setLocalSelectedStaff(Array.from(uniqueUserMap.values()));
      }
    }
  };

  const handleAddEmployees = () => {
    let finalSelected = [];

    // When from Busy Slot, skip filtering and deduplication
    if (isFromBusySlot) {
      finalSelected = [...localSelectedStaff];
    } else if (eventOrShiftTab) {
      // Filter out disabled users
      finalSelected = localSelectedStaff.filter(
        (item) => !shouldDisableMember(item),
      );
    } else {
      // Deduplicate based on userId
      const uniqueUserMap = new Map();
      localSelectedStaff.forEach((item) => {
        const userId = item.userId || item.user?.id;
        if (!uniqueUserMap.has(userId)) {
          uniqueUserMap.set(userId, item);
        }
      });
      finalSelected = Array.from(uniqueUserMap.values());
    }

    // Prepare data for saving
    const formattedMembers = finalSelected.map((item) => {
      const userId = item.userId || item.user?.id;
      const id = item.id;
      const memberData = memberDataCache[id] || item;

      if (item.isFromDetail || isFromBusySlot) {
        return {
          ...item,
          IsDeleted: false,
        };
      }

      let newStaffObjectToReturn = {
        ...memberData,
        id: 0,
        userId: userId,
        IsDeleted: false,
        isDeleted: false,
        user: memberData.user
          ? {
              ...memberData.user,
              fullName: `${memberData.user.firstName || ""} ${
                memberData.user.lastName || ""
              }`,
            }
          : null,
      };

      if (forEdit) {
        newStaffObjectToReturn.schedulerEventId = schedularEventId;
      }

      return newStaffObjectToReturn;
    });
    const selectedArray =
      forEdit && eventEmployeesSelected?.length
        ? eventEmployeesSelected
        : memberIds;
    const existingMembersToKeep = selectedArray
      .filter(
        (member) =>
          member.isFromDetail &&
          !finalSelected.some((item) => {
            if (forEdit) {
              const itemUserId = item.userId ?? item.user?.id;
              const memberUserId = member.userId ?? member.user?.id;
              const itemOfficeId = item.officeId ?? item.office?.id;
              const memberOfficeId = member.officeId ?? member.office?.id;
              return (
                itemUserId === memberUserId && itemOfficeId === memberOfficeId
              );
            }
            return item.id === member.id;
          }),
      )
      .map((member) => {
        const obj = {
          ...member,
          IsDeleted: true,
          isDeleted: true,
        };
        if (forEdit) {
          obj.schedulerEventId = schedularEventId;
        }
        return obj;
      });

    setMemberIds([...formattedMembers, ...existingMembersToKeep]);
    if (setSelectedStaff) {
      setSelectedStaff(finalSelected); // Only update parent on Select
    }
    closeStepOneModal(false);
  };

  const searchComp = (
    <div
      className={`search-box w-${isFromBusySlot ? 100 : officeId ? 50 : 100} ${
        styles["search-box"]
      }`}
    >
      <ExpandableSearchBar
        placeholder={t("accountOwner.searchByName")}
        searchValue={search}
        setSearchValue={onSearch}
        expanded={true}
      />
    </div>
  );
  const officeSelect = (
    <Select
      className="react-select-container w-50"
      classNamePrefix="react-select"
      options={officeList}
      value={selectedOfficeOption}
      onChange={handleOfficeChange}
      isSearchable={false}
    />
  );

  /**
   * Number of currently selected staff members shown in the counter.
   * For event/shift and busy-slot flows only selectable (non-disabled) members
   * count; elsewhere the same user selected across offices counts once.
   */
  const selectedCount =
    eventOrShiftTab || isFromBusySlot
      ? localSelectedStaff.filter((item) => !shouldDisableMember(item)).length
      : new Set(localSelectedStaff.map((item) => item.user?.id || item.userId))
          .size;

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => closeStepOneModal(false)}
      className="modal-dialog-centered modal-width-660 new-group-modal"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={() => closeStepOneModal(false)}>
        <img src={crossIcon} alt="close" />
      </span>

      <ModalBody>
        {/* Header */}
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          {t("notes.selectStaffMember")}
        </Text>

        {/* Search and filters */}
        {isFromBusySlot ? searchComp : !officeId && searchComp}

        {/* Filter dropdowns */}
        <div className={` ${styles["select_box_wrapper"]} mb-3`}>
          {isFromBusySlot ? officeSelect : officeId ? searchComp : officeSelect}
          <Select
            className={
              "react-select-container w-50" +
              (officeId && !isFromBusySlot ? " add-spacing" : "")
            }
            classNamePrefix="react-select"
            options={rolesData}
            value={selectedRoleOption}
            onChange={handleRoleChange}
            isSearchable={false}
          />
        </div>

        {/* Selection counter: always rendered, shows "0 Selected" when nothing is selected */}
        <Text
          size="12px"
          marginBottom="20px"
          marginTop="20px"
          weight="400"
          color="rgb(111, 119, 136)"
        >
          {selectedCount} {t("Selected")}
        </Text>

        <div
          className={`${styles.select_all_row} ${
            memberList?.length > 0 ? "" : styles.select_all_row_hidden
          } ch-checkbox c-field all-event-checkbox m-0`}
          aria-hidden={memberList?.length === 0}
        >
          <label>
            <input
              type="checkbox"
              onChange={handleAllChange}
              onMouseDown={preventCheckboxFocusScroll}
              checked={selectAll}
              disabled={isLoading || memberList?.length === 0}
            />
            <span>{t("staff.selectAll")}</span>
          </label>
        </div>

        <ul
          className={`modal-employee-list group-list ${styles.staff_member_list} ${
            isLoading || !memberList?.length
              ? styles.staff_member_list_centered
              : ""
          }`}
          id="scrollableDiv"
        >
          {isLoading ? (
            <li className="border-0">
              <Spinner animation="border" className="loader-spinner" />
            </li>
          ) : memberList?.length > 0 ? (
            memberList.map((item) => {
              const disabled = shouldDisableMember(item);
              const checked = isSelected(item) && !disabled;

              return (
                <li key={getStaffMemberRowKey(item)}>
                  <div
                    className={`${styles.border_list} ch-checkbox ${
                      disabled ? styles.disabled_list : ""
                    }`}
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => !disabled && handleSelectUser(item)}
                        onMouseDown={preventCheckboxFocusScroll}
                        disabled={disabled}
                      />
                      <span className="d-flex">
                        <img
                          src={item?.user?.profilePic || StaffDefaultImage}
                          alt="profile-pic"
                          className={styles.Default_staff_image}
                        />
                        <div>
                          {`${item?.user?.firstName || ""} ${
                            item?.user?.lastName || ""
                          }`}
                          <Text size="12px" weight="400" color="#87928D">
                            {`${
                              item?.designation?.name
                                ? item.designation.name +
                                  (isFromBusySlot
                                    ? ", "
                                    : officeId || !item?.office?.name
                                      ? ""
                                      : ", ")
                                : ""
                            }${
                              isFromBusySlot
                                ? item?.office?.name || ""
                                : officeId || !item?.office?.name
                                  ? ""
                                  : item?.office?.name
                            }`}
                          </Text>
                        </div>
                      </span>
                    </label>
                  </div>
                </li>
              );
            })
          ) : (
            <li>
              <Empty Message={t("noStaffMemberFound")} />
            </li>
          )}
        </ul>

        {/* Footer buttons */}
        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100 mt-4"
          title={t("accountOwner.addEmployees")}
          onClick={handleAddEmployees}
        >
          {t("staff.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3  mt-md-4"
          onClick={() => closeStepOneModal(false)}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(SelectStaffMemberModal);
